import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { CurrencyService } from '../currency.service';
import {
  CurrencyResponse,
  CurrencyRequest,
  CurrencyRateRequest,
  ExchangeRateRequest,
  CountryResponse,
  CurrencyConversionRequest,
} from '../models/currency.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-currency',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PageBreadcrumbComponent],
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.css'],
})
export class CurrencyComponent implements OnInit {
  activeTab: 'monedas' | 'cambio' = 'monedas';

  // Monedas
  currencies: CurrencyResponse[] = [];
  countries: CountryResponse[] = [];
  countryNameById = new Map<number, string>();
  loadingCurrencies = false;
  errorCurrencies = '';

  // Exchange
  exchangeForm: ExchangeRateRequest = {
    fromCurrencyCode: '',
    toCurrencyCode: '',
    amount: 1,
  };
  exchangeResult: CurrencyConversionRequest | null = null;
  loadingExchange = false;
  errorExchange = '';

  // Currency modal
  showCurrencyModal = false;
  editingCurrency: CurrencyResponse | null = null;
  currencyForm: CurrencyRequest = {
    intIdCountry: 0,
    name: '',
    code: '',
    prefix: '',
  };
  savingCurrency = false;
  currencyErrors: Partial<Record<keyof CurrencyRequest, string>> = {};

  // Rate modal
  showRateModal = false;
  rateTargetCurrency: CurrencyResponse | null = null;
  rateForm: CurrencyRateRequest = { rateUsd: 0 };
  savingRate = false;

  constructor(
    private currencyService: CurrencyService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadCurrencies();
    this.loadCountries();
  }

  onTabChange(tab: string): void {
    this.activeTab = tab as 'monedas' | 'cambio';
  }

  loadCurrencies(): void {
    this.loadingCurrencies = true;
    this.errorCurrencies = '';
    this.currencyService.getCurrencies().subscribe({
      next: (data) => {
        this.currencies = data;
        this.loadingCurrencies = false;
      },
      error: () => {
        this.errorCurrencies = 'Error al cargar las monedas.';
        this.loadingCurrencies = false;
      },
    });
  }

  loadCountries(): void {
    this.currencyService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
        this.countryNameById = new Map(data.map((c) => [c.id, c.name]));
      },
      error: () => {
        // Non-critical; silently ignore
      },
    });
  }

  isRateStale(rateUpdatedAt?: string): boolean {
    if (!rateUpdatedAt) return true;
    const updated = new Date(rateUpdatedAt).getTime();
    const now = Date.now();
    return now - updated > 24 * 60 * 60 * 1000;
  }

  // Currency modal
  openCreateCurrency(): void {
    this.editingCurrency = null;
    this.currencyForm = { intIdCountry: 0, name: '', code: '', prefix: '' };
    this.currencyErrors = {};
    this.showCurrencyModal = true;
  }

  openEditCurrency(c: CurrencyResponse): void {
    this.editingCurrency = c;
    this.currencyForm = {
      intIdCountry: c.intIdCountry,
      name: c.name,
      code: c.code,
      prefix: c.prefix,
    };
    this.currencyErrors = {};
    this.showCurrencyModal = true;
  }

  private validateCurrencyForm(): boolean {
    const errors: Partial<Record<keyof CurrencyRequest, string>> = {};

    if (!this.currencyForm.intIdCountry) {
      errors.intIdCountry = 'Selecciona un país.';
    }
    if (!this.currencyForm.name?.trim()) {
      errors.name = 'El nombre es obligatorio.';
    }
    if (!this.currencyForm.code?.trim()) {
      errors.code = 'El código es obligatorio.';
    } else {
      const code = this.currencyForm.code.trim().toUpperCase();
      const dup = this.currencies.some(
        (c) =>
          c.code?.toUpperCase() === code && c.id !== this.editingCurrency?.id,
      );
      if (dup) {
        errors.code = `La moneda con código "${code}" ya está registrada.`;
      }
    }
    if (!this.currencyForm.prefix?.trim()) {
      errors.prefix = 'El prefijo es obligatorio.';
    }

    this.currencyErrors = errors;
    return Object.keys(errors).length === 0;
  }

  clearCurrencyError(field: keyof CurrencyRequest): void {
    if (this.currencyErrors[field]) {
      this.currencyErrors = { ...this.currencyErrors, [field]: '' };
    }
  }

  saveCurrency(): void {
    if (!this.validateCurrencyForm()) return;
    this.savingCurrency = true;
    const obs = this.editingCurrency
      ? this.currencyService.updateCurrency(
          this.editingCurrency.id,
          this.currencyForm,
        )
      : this.currencyService.createCurrency(this.currencyForm);
    obs.subscribe({
      next: () => {
        this.savingCurrency = false;
        this.showCurrencyModal = false;
        this.loadCurrencies();
      },
      error: (err) => {
        this.savingCurrency = false;
        if (err?.status === 409) {
          this.currencyErrors = {
            ...this.currencyErrors,
            code: 'La moneda ya se encuentra registrada en el sistema.',
          };
        } else {
          this.toast.error({
            title: 'Error',
            message: 'No se pudo guardar la moneda.',
          });
        }
      },
    });
  }

  async deleteCurrency(c: CurrencyResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar moneda',
      message: `¿Eliminar la moneda "${c.name}"?`,
    });
    if (!ok) return;
    this.currencyService.deleteCurrency(c.id).subscribe({
      next: () => {
        this.loadCurrencies();
        this.toast.deleted('Moneda');
      },
      error: () => {
        this.toast.error({
          title: 'Error',
          message: 'Error al eliminar la moneda.',
        });
      },
    });
  }

  // Rate modal
  openRateModal(c: CurrencyResponse): void {
    this.rateTargetCurrency = c;
    this.rateForm = { rateUsd: c.rateUsd ?? 0 };
    this.showRateModal = true;
  }

  saveRate(): void {
    if (!this.rateTargetCurrency) return;
    this.savingRate = true;
    this.currencyService
      .updateCurrencyRate(this.rateTargetCurrency.id, this.rateForm)
      .subscribe({
        next: () => {
          this.savingRate = false;
          this.showRateModal = false;
          this.loadCurrencies();
          this.toast.updated('Tasa de cambio');
        },
        error: () => {
          this.savingRate = false;
          this.toast.error({
            title: 'Error',
            message: 'Error al guardar la tasa de cambio.',
          });
        },
      });
  }

  // Exchange
  submitExchange(): void {
    this.loadingExchange = true;
    this.errorExchange = '';
    this.exchangeResult = null;
    this.currencyService.getExchangeRate(this.exchangeForm).subscribe({
      next: (result) => {
        this.exchangeResult = result;
        this.loadingExchange = false;
      },
      error: (err) => {
        this.errorExchange = this.resolveExchangeError(err);
        this.loadingExchange = false;
      },
    });
  }

  //helpers
  getCountryName(c: CurrencyResponse): string {
    return c.countryName ?? this.countryNameById.get(c.intIdCountry) ?? '—';
  }

  /** Moneda oficial por país (ISO 3166-1 alpha-2 → ISO 4217) */
  private readonly currencyByCountry: Record<
    string,
    { name: string; code: string; symbol: string }
  > = {
    PE: { name: 'Sol Peruano', code: 'PEN', symbol: 'S/' },
    CO: { name: 'Peso Colombiano', code: 'COP', symbol: '$' },
    CL: { name: 'Peso Chileno', code: 'CLP', symbol: '$' },
    AR: { name: 'Peso Argentino', code: 'ARS', symbol: '$' },
    BR: { name: 'Real Brasileño', code: 'BRL', symbol: 'R$' },
    US: { name: 'Dólar Estadounidense', code: 'USD', symbol: '$' },
    MX: { name: 'Peso Mexicano', code: 'MXN', symbol: '$' },
    CA: { name: 'Dólar Canadiense', code: 'CAD', symbol: '$' },
    AU: { name: 'Dólar Australiano', code: 'AUD', symbol: '$' },
    JP: { name: 'Yen Japonés', code: 'JPY', symbol: '¥' },
    CN: { name: 'Yuan Chino', code: 'CNY', symbol: '¥' },
    IN: { name: 'Rupia India', code: 'INR', symbol: '₹' },
    GB: { name: 'Libra Esterlina', code: 'GBP', symbol: '£' },
    // Eurozona (mismo EUR para varios países)
    ES: { name: 'Euro', code: 'EUR', symbol: '€' },
    FR: { name: 'Euro', code: 'EUR', symbol: '€' },
    DE: { name: 'Euro', code: 'EUR', symbol: '€' },
    IT: { name: 'Euro', code: 'EUR', symbol: '€' },
    PT: { name: 'Euro', code: 'EUR', symbol: '€' },
  };

  onCountryChange(countryId: number): void {
    this.clearCurrencyError('intIdCountry');

    const country = this.countries.find((c) => c.id === countryId);
    const iso = country?.alphabeticCode?.toUpperCase();
    if (!iso) return;

    const currency = this.currencyByCountry[iso];
    if (!currency) return; // país sin mapeo → el usuario llena manual, sin romper nada

    this.currencyForm.name = currency.name;
    this.currencyForm.code = currency.code;
    this.currencyForm.prefix = currency.symbol;

    // limpia los errores
    this.clearCurrencyError('name');
    this.clearCurrencyError('code');
    this.clearCurrencyError('prefix');
  }

  // Resuelve el mensaje de error para las solicitudes de tipo de cambio
  private resolveExchangeError(err: any): string {
    const e = err?.error;
    if (e?.details && typeof e.details === 'object') {
      const detalles = Object.values(e.details).filter(Boolean).join(' · ');
      if (detalles) return detalles;
    }
    if (e?.message && typeof e.message === 'string') return e.message;
    switch (err?.status) {
      case 0:
        return 'No se pudo conectar con el servidor. Verifica tu conexión.';
      case 400:
        return 'Los datos de la consulta no son válidos.';
      case 404:
        return 'No hay una tasa disponible para el par seleccionado.';
      case 502:
      case 503:
        return 'La fuente de tipo de cambio no está disponible. Intenta más tarde.';
      default:
        return 'Error al consultar el tipo de cambio. Intenta nuevamente.';
    }
  }
}
